import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  Label,
  TimePicker,
} from '@startupquickstart/frontend';
import { Plus, X } from 'lucide-react';
import { useCallback, useMemo } from 'react';

interface TimeRange {
  open: string;
  close: string;
}

interface DayHours {
  is24_7?: boolean;
  isClosed?: boolean;
  ranges?: TimeRange[];
}

interface SpecialHours {
  date: string;
  name: string;
  hours: DayHours;
}

interface BusinessHours extends Record<string, DayHours | SpecialHours[] | undefined> {
  monday?: DayHours;
  tuesday?: DayHours;
  wednesday?: DayHours;
  thursday?: DayHours;
  friday?: DayHours;
  saturday?: DayHours;
  sunday?: DayHours;
  specialHours?: SpecialHours[];
}

interface BusinessHoursEditorProps {
  value?: BusinessHours | Record<string, unknown>;
  onChange: (value: BusinessHours) => void;
  error?: string;
}

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
] as const;

/**
 * Sanitize business hours data to only include expected keys
 * Removes any extra fields that might come from the backend
 */
function sanitizeBusinessHours(hours: BusinessHours): BusinessHours {
  const sanitized: BusinessHours = {};

  // Sanitize day hours
  for (const day of DAYS_OF_WEEK) {
    const dayHours = hours[day.key as keyof BusinessHours];
    if (dayHours && !Array.isArray(dayHours)) {
      const typedDayHours = dayHours as DayHours;
      sanitized[day.key as keyof BusinessHours] = {
        is24_7: typedDayHours.is24_7,
        isClosed: typedDayHours.isClosed,
        ranges: (typedDayHours.ranges || []).map((range) => ({
          open: range.open,
          close: range.close,
        })),
      };
    }
  }

  // Sanitize special hours
  if (hours.specialHours && Array.isArray(hours.specialHours)) {
    sanitized.specialHours = hours.specialHours.map((special) => ({
      date: special.date,
      name: special.name,
      hours: {
        is24_7: special.hours.is24_7,
        isClosed: special.hours.isClosed,
        ranges: (special.hours.ranges || []).map((range) => ({
          open: range.open,
          close: range.close,
        })),
      },
    }));
  }

  return sanitized;
}

/**
 * Business hours editor with support for multiple time ranges per day
 * Supports 24/7 and closed options
 */
export function BusinessHoursEditor({ value = {}, onChange, error }: BusinessHoursEditorProps) {
  // Use value directly, no internal state needed
  const hours = useMemo(() => (value as BusinessHours) || {}, [value]);

  /**
   * Toggle 24/7 for a day
   */
  const toggle24_7 = useCallback(
    (day: string, checked: boolean) => {
      const newHours = { ...hours };
      newHours[day as keyof BusinessHours] = {
        is24_7: checked,
        isClosed: false,
        ranges: checked ? [] : [{ open: '09:00', close: '17:00' }],
      };
      onChange(sanitizeBusinessHours(newHours));
    },
    [hours, onChange],
  );

  /**
   * Toggle closed for a day
   */
  const toggleClosed = useCallback(
    (day: string, checked: boolean) => {
      const newHours = { ...hours };
      newHours[day as keyof BusinessHours] = {
        is24_7: false,
        isClosed: checked,
        ranges: checked ? [] : [{ open: '09:00', close: '17:00' }],
      };
      onChange(sanitizeBusinessHours(newHours));
    },
    [hours, onChange],
  );

  /**
   * Update a time range for a day
   */
  const updateTimeRange = useCallback(
    (day: string, index: number, field: 'open' | 'close', timeValue: string) => {
      const newHours = { ...hours };
      const dayHours = (newHours[day as keyof BusinessHours] as DayHours) || { ranges: [] };
      const ranges = dayHours.ranges || [];
      ranges[index] = { ...ranges[index], [field]: timeValue };
      newHours[day as keyof BusinessHours] = { ...dayHours, ranges };
      onChange(sanitizeBusinessHours(newHours));
    },
    [hours, onChange],
  );

  /**
   * Add a new time range for a day
   */
  const addTimeRange = useCallback(
    (day: string) => {
      const newHours = { ...hours };
      const dayHours = (newHours[day as keyof BusinessHours] as DayHours) || { ranges: [] };
      const ranges = dayHours.ranges || [];
      ranges.push({ open: '09:00', close: '17:00' });
      newHours[day as keyof BusinessHours] = { ...dayHours, ranges };
      onChange(sanitizeBusinessHours(newHours));
    },
    [hours, onChange],
  );

  /**
   * Remove a time range from a day
   */
  const removeTimeRange = useCallback(
    (day: string, index: number) => {
      const newHours = { ...hours };
      const dayHours = (newHours[day as keyof BusinessHours] as DayHours) || { ranges: [] };
      const ranges = (dayHours.ranges || []).filter((_: TimeRange, i: number) => i !== index);
      newHours[day as keyof BusinessHours] = { ...dayHours, ranges };
      onChange(sanitizeBusinessHours(newHours));
    },
    [hours, onChange],
  );

  /**
   * Get day hours or default
   */
  const getDayHours = useCallback(
    (day: string): DayHours => {
      return (hours[day as keyof BusinessHours] as DayHours) || { ranges: [] };
    },
    [hours],
  );

  /**
   * Add special hours entry
   */
  const addSpecialHours = useCallback(() => {
    const newHours = { ...hours };
    const specialHours = newHours.specialHours || [];
    specialHours.push({
      date: '',
      name: '',
      hours: { ranges: [{ open: '09:00', close: '17:00' }] },
    });
    newHours.specialHours = specialHours;
    onChange(sanitizeBusinessHours(newHours));
  }, [hours, onChange]);

  /**
   * Remove special hours entry
   */
  const removeSpecialHours = useCallback(
    (index: number) => {
      const newHours = { ...hours };
      const specialHours = (newHours.specialHours || []).filter((_, i) => i !== index);
      newHours.specialHours = specialHours;
      onChange(sanitizeBusinessHours(newHours));
    },
    [hours, onChange],
  );

  /**
   * Update special hours field
   */
  const updateSpecialHoursField = useCallback(
    (index: number, field: 'date' | 'name', value: string) => {
      const newHours = { ...hours };
      const specialHours = newHours.specialHours || [];
      specialHours[index] = { ...specialHours[index], [field]: value };
      newHours.specialHours = specialHours;
      onChange(sanitizeBusinessHours(newHours));
    },
    [hours, onChange],
  );

  /**
   * Toggle 24/7 for special hours
   */
  const toggleSpecialHours24_7 = useCallback(
    (index: number, checked: boolean) => {
      const newHours = { ...hours };
      const specialHours = newHours.specialHours || [];
      specialHours[index] = {
        ...specialHours[index],
        hours: {
          is24_7: checked,
          isClosed: false,
          ranges: checked ? [] : [{ open: '09:00', close: '17:00' }],
        },
      };
      newHours.specialHours = specialHours;
      onChange(sanitizeBusinessHours(newHours));
    },
    [hours, onChange],
  );

  /**
   * Toggle closed for special hours
   */
  const toggleSpecialHoursClosed = useCallback(
    (index: number, checked: boolean) => {
      const newHours = { ...hours };
      const specialHours = newHours.specialHours || [];
      specialHours[index] = {
        ...specialHours[index],
        hours: {
          is24_7: false,
          isClosed: checked,
          ranges: checked ? [] : [{ open: '09:00', close: '17:00' }],
        },
      };
      newHours.specialHours = specialHours;
      onChange(sanitizeBusinessHours(newHours));
    },
    [hours, onChange],
  );

  /**
   * Update special hours time range
   */
  const updateSpecialHoursTimeRange = useCallback(
    (specialIndex: number, rangeIndex: number, field: 'open' | 'close', timeValue: string) => {
      const newHours = { ...hours };
      const specialHours = newHours.specialHours || [];
      const ranges = specialHours[specialIndex].hours.ranges || [];
      ranges[rangeIndex] = { ...ranges[rangeIndex], [field]: timeValue };
      specialHours[specialIndex] = {
        ...specialHours[specialIndex],
        hours: { ...specialHours[specialIndex].hours, ranges },
      };
      newHours.specialHours = specialHours;
      onChange(sanitizeBusinessHours(newHours));
    },
    [hours, onChange],
  );

  /**
   * Add time range to special hours
   */
  const addSpecialHoursTimeRange = useCallback(
    (index: number) => {
      const newHours = { ...hours };
      const specialHours = newHours.specialHours || [];
      const ranges = specialHours[index].hours.ranges || [];
      ranges.push({ open: '09:00', close: '17:00' });
      specialHours[index] = {
        ...specialHours[index],
        hours: { ...specialHours[index].hours, ranges },
      };
      newHours.specialHours = specialHours;
      onChange(sanitizeBusinessHours(newHours));
    },
    [hours, onChange],
  );

  /**
   * Remove time range from special hours
   */
  const removeSpecialHoursTimeRange = useCallback(
    (specialIndex: number, rangeIndex: number) => {
      const newHours = { ...hours };
      const specialHours = newHours.specialHours || [];
      const ranges = (specialHours[specialIndex].hours.ranges || []).filter(
        (_, i) => i !== rangeIndex,
      );
      specialHours[specialIndex] = {
        ...specialHours[specialIndex],
        hours: { ...specialHours[specialIndex].hours, ranges },
      };
      newHours.specialHours = specialHours;
      onChange(sanitizeBusinessHours(newHours));
    },
    [hours, onChange],
  );

  return (
    <div className="grid gap-4">
      <div className="border rounded-lg divide-y">
        {DAYS_OF_WEEK.map(({ key, label: dayLabel }) => {
          const dayHours = getDayHours(key);
          const is24_7 = dayHours.is24_7 || false;
          const isClosed = dayHours.isClosed || false;
          const ranges = dayHours.ranges || [];

          return (
            <div key={key} className="p-4 grid gap-3">
              {/* Day header with options */}
              <div className="flex items-center justify-between">
                <div className="font-medium">{dayLabel}</div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`${key}-24-7`}
                      checked={is24_7}
                      onCheckedChange={checked => toggle24_7(key, checked as boolean)}
                    />
                    <label
                      htmlFor={`${key}-24-7`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      24/7
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`${key}-closed`}
                      checked={isClosed}
                      onCheckedChange={checked => toggleClosed(key, checked as boolean)}
                    />
                    <label
                      htmlFor={`${key}-closed`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Closed
                    </label>
                  </div>
                </div>
              </div>

              {/* Time ranges */}
              {!is24_7 && !isClosed && (
                <div className="grid gap-2">
                  {ranges.length === 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addTimeRange(key)}
                      className="w-fit"
                    >
                      <Plus />
                      Add hours
                    </Button>
                  )}
                  {ranges.map((range, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <TimePicker
                        time={range.open}
                        onTimeChange={(time: string) => updateTimeRange(key, index, 'open', time)}
                        className="flex-1"
                      />
                      <span className="text-muted-foreground">to</span>
                      <TimePicker
                        time={range.close}
                        onTimeChange={(time: string) => updateTimeRange(key, index, 'close', time)}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeTimeRange(key, index)}
                        className="h-10 w-10"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {ranges.length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addTimeRange(key)}
                      className="w-fit"
                    >
                      <Plus />
                      Add hours
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Special Hours Section */}
      <div className="grid gap-3">
        <div className="flex items-center justify-between">
          <Label className="text-base font-semibold">Special Hours</Label>
          <Button type="button" variant="outline" size="sm" onClick={addSpecialHours}>
            <Plus className="h-4 w-4 mr-2" />
            Add Special Hours
          </Button>
        </div>

        {(hours.specialHours || []).length > 0 && (
          <div className="border rounded-lg divide-y">
            {(hours.specialHours || []).map((special, specialIndex) => {
              const is24_7 = special.hours.is24_7 || false;
              const isClosed = special.hours.isClosed || false;
              const ranges = special.hours.ranges || [];

              return (
                <div key={specialIndex} className="p-4 grid gap-3">
                  {/* Special hours header */}
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="grid gap-2">
                      <Label>Date</Label>
                      <DatePicker
                        date={special.date ? new Date(special.date) : undefined}
                        onDateChange={(date: Date | undefined) =>
                          updateSpecialHoursField(
                            specialIndex,
                            'date',
                            date?.toISOString().split('T')[0] || '',
                          )
                        }
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Name (e.g., Holiday, Event)</Label>
                      <Input
                        value={special.name}
                        onChange={e =>
                          updateSpecialHoursField(specialIndex, 'name', e.target.value)
                        }
                        placeholder="e.g., Christmas, Thanksgiving"
                      />
                    </div>
                  </div>

                  {/* Special hours options */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`special-${specialIndex}-24-7`}
                        checked={is24_7}
                        onCheckedChange={checked =>
                          toggleSpecialHours24_7(specialIndex, checked as boolean)
                        }
                      />
                      <label
                        htmlFor={`special-${specialIndex}-24-7`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        24/7
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`special-${specialIndex}-closed`}
                        checked={isClosed}
                        onCheckedChange={checked =>
                          toggleSpecialHoursClosed(specialIndex, checked as boolean)
                        }
                      />
                      <label
                        htmlFor={`special-${specialIndex}-closed`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Closed
                      </label>
                    </div>
                  </div>

                  {/* Time ranges for special hours */}
                  {!is24_7 && !isClosed && (
                    <div className="grid gap-2">
                      {ranges.length === 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addSpecialHoursTimeRange(specialIndex)}
                          className="w-fit"
                        >
                          <Plus />
                          Add hours
                        </Button>
                      )}
                      {ranges.map((range, rangeIndex) => (
                        <div key={rangeIndex} className="flex items-center gap-2">
                          <TimePicker
                            time={range.open}
                            onTimeChange={(time: string) =>
                              updateSpecialHoursTimeRange(specialIndex, rangeIndex, 'open', time)
                            }
                            className="flex-1"
                          />
                          <span className="text-muted-foreground">to</span>
                          <TimePicker
                            time={range.close}
                            onTimeChange={(time: string) =>
                              updateSpecialHoursTimeRange(specialIndex, rangeIndex, 'close', time)
                            }
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => removeSpecialHoursTimeRange(specialIndex, rangeIndex)}
                            className="h-10 w-10"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      {ranges.length > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addSpecialHoursTimeRange(specialIndex)}
                          className="w-fit"
                        >
                          <Plus />
                          Add hours
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Remove button */}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeSpecialHours(specialIndex)}
                    >
                      <X />
                      Remove
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
